import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtError, TERRITOIRES, CONFORMITE_COLORS, eur } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Search, Phone, Mail, Globe, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const CATS = [["", "Tous"], ["client", "Clients"], ["prospect", "Prospects"], ["fournisseur", "Fournisseurs"],
  ["sous-traitant", "Sous-traitants"], ["transporteur", "Transporteurs"], ["partenaire", "Partenaires"]];
const PIPELINE = ["Nouveau", "À qualifier", "Visite à programmer", "Étude", "Chiffrage", "Devis envoyé", "Relance", "Négociation", "Gagné", "Perdu", "Reporté"];

const EMPTY = { raison_sociale: "", nom_commercial: "", categorie: "prospect", siren: "", siret: "",
  adresse: "", commune: "", territoire: "Guadeloupe", telephone: "", whatsapp: "", email: "", site_web: "",
  contact_nom: "", contact_fonction: "", secteur: "", source_contact: "", tags: "",
  stade: "Nouveau", besoin: "", montant_estime: 0, probabilite: 0, prochaine_action: "", date_relance: "", note: "" };

export default function Crm() {
  const [contacts, setContacts] = useState([]);
  const [cat, setCat] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [histText, setHistText] = useState("");
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/contacts", { params: { categorie: cat || undefined, q: search || undefined } })
    .then((r) => setContacts(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);

  useEffect(() => {
    const n = params.get("new");
    if (n) {
      setForm({ ...EMPTY, categorie: ["client", "prospect", "fournisseur", "sous-traitant"].includes(n) ? n : "prospect" });
      setOpen(true);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await api.put(`/contacts/${form.id}`, form);
      else await api.post("/contacts", form);
      toast.success("Contact enregistré");
      setOpen(false); setForm(EMPTY); setDetail(null); load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const addHist = async () => {
    if (!histText.trim() || !detail) return;
    await api.post(`/contacts/${detail.id}/historique`, { texte: histText }).catch(() => {});
    setHistText("");
    const { data } = await api.get(`/contacts/${detail.id}`);
    setDetail(data);
  };

  return (
    <div className="space-y-5" data-testid="crm-page">
      <div className="flex items-center justify-between gap-2">
        <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">CRM</h1>
        <Button data-testid="new-contact-button" onClick={() => { setForm(EMPTY); setOpen(true); }} className="bg-amber-500 text-slate-900 font-bold hover:bg-amber-400 h-12">
          <Plus className="h-5 w-5 mr-1" /> Nouveau
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="crm-filters">
        {CATS.map(([v, l]) => (
          <button key={v} data-testid={`crm-filter-${v || "tous"}`} onClick={() => setCat(v)}
            className={`px-4 py-2.5 rounded-xl font-bold text-sm border-2 touch-target ${cat === v ? "bg-amber-500 text-slate-900 border-amber-500" : "bg-card border-border"}`}>{l}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input data-testid="crm-search" placeholder="Rechercher (nom, commune…)" value={search} onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()} className="py-3.5" />
        <Button data-testid="crm-search-button" onClick={load} variant="outline" className="h-13"><Search className="h-5 w-5" /></Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="contacts-list">
        {contacts.map((c) => (
          <button key={c.id} data-testid={`contact-card-${c.id}`} onClick={() => setDetail(c)}
            className="bg-card rounded-xl border-2 border-border hover:border-amber-500 p-4 text-left transition-all hover:shadow-md">
            <div className="flex items-start justify-between gap-2">
              <div className="font-bold text-sm leading-tight">{c.raison_sociale}</div>
              {c.categorie === "sous-traitant" && (
                <span className={`${CONFORMITE_COLORS[c.statut_conformite || "GRIS"]} h-3.5 w-3.5 rounded-full shrink-0 mt-1`} title={`Conformité ${c.statut_conformite}`} />
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">{c.nom_commercial && `${c.nom_commercial} • `}{c.categorie}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" />{c.commune || "—"} ({c.territoire})</div>
            {c.categorie === "prospect" && <div className="mt-2 text-[10px] font-bold uppercase bg-sky-100 dark:bg-sky-950 text-sky-800 dark:text-sky-200 px-2 py-1 rounded inline-block">{c.stade}</div>}
          </button>
        ))}
        {contacts.length === 0 && <p className="text-sm text-muted-foreground col-span-full" data-testid="contacts-empty">Aucun contact.</p>}
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contact-detail-modal">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{detail.raison_sociale}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                {detail.nom_commercial && <div><span className="font-bold">Nom commercial :</span> {detail.nom_commercial}</div>}
                <div className="flex gap-2 flex-wrap">
                  <span className="bg-muted px-2 py-1 rounded font-bold text-xs uppercase">{detail.categorie}</span>
                  {detail.categorie === "sous-traitant" && (
                    <span className={`px-2 py-1 rounded font-bold text-xs uppercase text-white ${CONFORMITE_COLORS[detail.statut_conformite || "GRIS"]}`}>
                      Conformité {detail.statut_conformite || "GRIS"}
                    </span>
                  )}
                </div>
                {detail.siren && <div className="font-mono-num text-xs">SIREN {detail.siren} {detail.siret && `— SIRET ${detail.siret}`}</div>}
                {detail.adresse && <div className="flex gap-1 items-start"><MapPin className="h-4 w-4 mt-0.5 shrink-0" />{detail.adresse}</div>}
                <div className="flex gap-3 flex-wrap">
                  {detail.telephone && <a href={`tel:${detail.telephone}`} data-testid="contact-call" className="flex items-center gap-1 font-bold text-amber-600"><Phone className="h-4 w-4" />{detail.telephone}</a>}
                  {detail.email && <a href={`mailto:${detail.email}`} data-testid="contact-email" className="flex items-center gap-1 font-bold text-amber-600"><Mail className="h-4 w-4" />E-mail</a>}
                  {detail.whatsapp && <a href={`https://wa.me/${detail.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-1 font-bold text-emerald-600"><MessageCircle className="h-4 w-4" />WhatsApp</a>}
                  {detail.site_web && <a href={detail.site_web} target="_blank" rel="noreferrer" data-testid="contact-website" className="flex items-center gap-1 font-bold text-sky-600"><Globe className="h-4 w-4" />Site</a>}
                </div>
                {detail.secteur && <div><span className="font-bold">Activité :</span> {detail.secteur}</div>}
                {detail.source_contact && <div className="text-xs text-muted-foreground">Source : {detail.source_contact}</div>}
                {detail.categorie === "prospect" && (
                  <div className="bg-muted rounded-lg p-3 space-y-1">
                    <div className="font-bold text-xs uppercase">Pipeline : {detail.stade}</div>
                    {detail.besoin && <div className="text-sm">{detail.besoin}</div>}
                    {detail.montant_estime > 0 && <div className="font-mono-num font-bold">{eur(detail.montant_estime)} ({detail.probabilite}%)</div>}
                    {detail.prochaine_action && <div className="text-sm">▸ {detail.prochaine_action} {detail.date_relance && `— ${detail.date_relance}`}</div>}
                  </div>
                )}
                {detail.conformite_notes && <div className="bg-red-50 dark:bg-red-950 border border-red-300 rounded-lg p-3 text-xs font-semibold">{detail.conformite_notes}</div>}
                {detail.tarifs_notes && <div className="bg-muted rounded-lg p-3 text-xs">{detail.tarifs_notes}</div>}
                <Button data-testid="contact-edit" variant="outline" className="w-full font-bold"
                  onClick={() => { setForm({ ...EMPTY, ...detail }); setOpen(true); setDetail(null); }}>
                  Modifier la fiche
                </Button>
                <div className="border-t border-border pt-3">
                  <div className="font-bold text-xs uppercase mb-2">Historique</div>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {[...(detail.historique || [])].reverse().map((h, i) => (
                      <div key={i} className="text-xs bg-muted rounded p-2"><span className="font-bold">{h.auteur}</span> — {h.texte} <span className="text-muted-foreground">({new Date(h.date).toLocaleDateString("fr-FR")})</span></div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Input data-testid="historique-input" placeholder="Ajouter une note…" value={histText} onChange={(e) => setHistText(e.target.value)} />
                    <Button data-testid="historique-add" onClick={addHist} className="bg-amber-500 text-slate-900 font-bold">+</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contact-form-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{form.id ? "Modifier" : "Nouveau"} contact</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <Input data-testid="contact-raison" required placeholder="Raison sociale *" value={form.raison_sociale} onChange={(e) => set("raison_sociale", e.target.value)} className="py-3.5" />
            <Input data-testid="contact-nom-commercial" placeholder="Nom commercial" value={form.nom_commercial} onChange={(e) => set("nom_commercial", e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="contact-categorie" value={form.categorie} onChange={(e) => set("categorie", e.target.value)} className="h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold">
                {CATS.slice(1).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                <option value="collectivite">Collectivité</option><option value="bureau_etudes">Bureau d'études</option>
                <option value="architecte">Architecte</option><option value="loueur">Loueur</option><option value="autre">Autre</option>
              </select>
              <select data-testid="contact-territoire" value={form.territoire} onChange={(e) => set("territoire", e.target.value)} className="h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold">
                {TERRITOIRES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="contact-siren" placeholder="SIREN" value={form.siren} onChange={(e) => set("siren", e.target.value)} />
              <Input data-testid="contact-siret" placeholder="SIRET" value={form.siret} onChange={(e) => set("siret", e.target.value)} />
            </div>
            <Input data-testid="contact-adresse" placeholder="Adresse" value={form.adresse} onChange={(e) => set("adresse", e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="contact-commune" placeholder="Commune" value={form.commune} onChange={(e) => set("commune", e.target.value)} />
              <Input data-testid="contact-telephone" placeholder="Téléphone" value={form.telephone} onChange={(e) => set("telephone", e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="contact-email" type="email" placeholder="E-mail" value={form.email} onChange={(e) => set("email", e.target.value)} />
              <Input data-testid="contact-whatsapp" placeholder="WhatsApp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} />
            </div>
            <Input data-testid="contact-secteur" placeholder="Secteur / activité" value={form.secteur} onChange={(e) => set("secteur", e.target.value)} />
            {form.categorie === "prospect" && (
              <>
                <select data-testid="contact-stade" value={form.stade} onChange={(e) => set("stade", e.target.value)} className="w-full h-12 rounded-lg border border-input bg-background px-3 text-sm font-semibold">
                  {PIPELINE.map((s) => <option key={s}>{s}</option>)}
                </select>
                <Input data-testid="contact-besoin" placeholder="Besoin" value={form.besoin} onChange={(e) => set("besoin", e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="contact-montant" type="number" step="any" placeholder="Montant estimé €" value={form.montant_estime} onChange={(e) => set("montant_estime", e.target.value)} />
                  <Input data-testid="contact-relance" type="date" value={form.date_relance} onChange={(e) => set("date_relance", e.target.value)} />
                </div>
              </>
            )}
            <Button data-testid="contact-submit" type="submit" className="w-full h-14 bg-amber-500 text-slate-900 font-bold hover:bg-amber-400">ENREGISTRER</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
